import type { ResourceAttribution } from "@/types_api/backend/transport/get_metric_response"
import type { ListMetricsRequest } from "@/types_api/backend/transport/list_metrics_request"
import type { MetricThresholdResource } from "@/types/api/MetricThreshold"
import type { MetricRecordData } from "@/types/metrics/contracts"
import { isLlmJudgeManager } from "@/types/metrics/llm-judge-managers"
import { mergeManagerUnit } from "@/types/metrics/manager-units"

export type ListMetricsTransportRequest = ListMetricsRequest

interface TransportMetricThresholdResource {
  attribution?: ResourceAttribution | null
  name?: string | null
  comparison_operator?: MetricThresholdResource["comparison_operator"] | null
  target_float_upper?: number | null
  target_float_lower?: number | null
  target_values?: string[] | null
  source?: MetricThresholdResource["source"] | null
  create_time?: string | null
  update_time?: string | null
}

export interface TransportMetricResource {
  attribution?: ResourceAttribution | null
  id: string
  organization_id?: string | null
  metric_name: string
  metric_display_name: string
  metric_metadata?: MetricRecordData["metric_metadata"] | null
  description: string
  metric_manager: string
  locked?: boolean
  category?: string | null
  created_at?: string | null
  created_by?: string | null
  last_updated_at?: string | null
  last_updated_by?: string | null
  output_type?: string | null
  tags?: string[] | null
  organization_threshold?: TransportMetricThresholdResource | null
  current_version_id?: string | null
  current_version_number?: number | null
  dependency_metric_ids?: string[] | null
}

const METRICS_LIST_ORDER_BY = "metric_name"
const METRICS_LIST_PAGE_SIZE = 1000
const DEFAULT_THRESHOLD_SOURCE: MetricThresholdResource["source"] = "manual"

export const METRICS_LIST_TRANSPORT_REQUEST = {
  include_builtin: true,
  order_by: METRICS_LIST_ORDER_BY,
  page_size: METRICS_LIST_PAGE_SIZE,
} satisfies ListMetricsTransportRequest

function normalizeTransportThreshold(
  threshold: TransportMetricResource["organization_threshold"],
): MetricThresholdResource | null {
  if (!threshold?.comparison_operator) {
    return null
  }

  return {
    attribution: threshold.attribution,
    name: threshold.name ?? "",
    comparison_operator: threshold.comparison_operator,
    target_float_upper: threshold.target_float_upper ?? null,
    target_float_lower: threshold.target_float_lower ?? null,
    target_values: threshold.target_values ?? null,
    source: threshold.source ?? DEFAULT_THRESHOLD_SOURCE,
    create_time: threshold.create_time ?? "",
    update_time: threshold.update_time ?? "",
  }
}

export function mapTransportMetricToRecordData(
  metric: TransportMetricResource,
): MetricRecordData & { id: string } {
  const metricData = {
    attribution: metric.attribution,
    id: metric.id,
    organization_id: metric.organization_id ?? undefined,
    metric_name: metric.metric_name,
    metric_display_name: metric.metric_display_name,
    description: metric.description,
    metric_manager: metric.metric_manager,
    locked: metric.locked ?? metric.organization_id == null,
    category: metric.category ?? undefined,
    created_at: metric.created_at ?? undefined,
    created_by: metric.created_by ?? undefined,
    last_updated_at: metric.last_updated_at ?? undefined,
    last_updated_by: metric.last_updated_by ?? undefined,
    output_type: metric.output_type ?? undefined,
    tags: metric.tags ?? [],
    organization_threshold: normalizeTransportThreshold(
      metric.organization_threshold,
    ),
    current_version_id: metric.current_version_id ?? null,
    current_version_number: metric.current_version_number ?? null,
    dependency_metric_ids: metric.dependency_metric_ids ?? null,
    is_llm_judge: isLlmJudgeManager(metric.metric_manager),
  }

  return {
    ...metricData,
    metric_metadata: mergeManagerUnit({
      ...metricData,
      metric_metadata: metric.metric_metadata ?? {},
    }),
  }
}

export function mapTransportMetricsToRecordData(
  metrics: TransportMetricResource[],
): Record<string, MetricRecordData & { id: string }> {
  return metrics.reduce<Record<string, MetricRecordData & { id: string }>>(
    (accumulator, metric) => {
      const existingMetric = accumulator[metric.metric_name]
      if (!existingMetric || metric.organization_id) {
        accumulator[metric.metric_name] = mapTransportMetricToRecordData(metric)
      }
      return accumulator
    },
    {},
  )
}
